import { ColumnDef, Row } from '@tanstack/react-table';
import { IntlShape, useIntl } from 'react-intl';
import { Tooltip } from 'components/Tooltip';
import { LensRow, ProfitabilityBreakdownRow } from './types';
import { statusColorClass, statusSortValue } from 'components/profitability/profitabilityStatus';
import { StatusCell } from 'components/profitability/StatusCell';
import { cn } from 'utils/classNamesTailwind';
import { useModelService } from 'services/model/model.service';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';

export function useProfitabilityColumns(): ColumnDef<ProfitabilityBreakdownRow>[] {
  const { getMeasures } = useModelService();
  const intl = useIntl();

  const { createMeasureColumn } = useDataEntriesInTables();

  const [netSlsGmvValExtOD, netSlsCogRateOD, cm1RateOD, slsFeeRateOD, logCstRateOD, cm2ValOD, cm2RateOD] = getMeasures([
    'netSlsGmvValExtExpOD',
    'netSlsCogRateExtExpOD',
    'cm1RateExtExpOD',
    'netSlsFeeRateExtExpOD',
    'netSlsLogCostRateExtExpOD',
    'cm2ValExtExpOD',
    'cm2RateExtExpOD',
  ]);

  return [
    createMeasureColumn(netSlsGmvValExtOD, { ignoreComparison: true }),
    createMeasureColumn(netSlsCogRateOD, { ignoreComparison: true }),
    createMeasureColumn(cm1RateOD, { className: 'font-bold', ignoreComparison: true }),
    createMeasureColumn(slsFeeRateOD, { ignoreComparison: true }),
    createMeasureColumn(logCstRateOD, { ignoreComparison: true }),
    createMeasureColumn(cm2ValOD, { ignoreComparison: true }),
    createMeasureColumn(cm2RateOD, {
      ignoreComparison: true,
      className: row => cn('font-bold', statusColorClass((row as unknown as Row<LensRow>).original.status).text),
    }),

    buildStatusColumn(intl),
  ];
}

export function useProfitabilityLensColumns(): ColumnDef<LensRow>[] {
  const { getMeasures } = useModelService();

  const { createMeasureColumn } = useDataEntriesInTables();
  const [netSlsGmvValExtOD, netSlsCogValOD, cm1RateOD, cm1ValOD, slsFeeValOD, logCstValOD, cm2ValOD, cm2RateOD] =
    getMeasures([
      'netSlsGmvValExtExpOD',
      'netSlsCogValExtExpOD',
      'cm1RateExtExpOD',
      'cm1ValExtExpOD',
      'netSlsFeeValExtExpOD',
      'netSlsLogCostValExtExpOD',
      'cm2ValExtExpOD',
      'cm2RateExtExpOD',
    ]);

  return [
    createMeasureColumn(netSlsGmvValExtOD),
    createMeasureColumn(netSlsCogValOD, { ignoreComparison: true }),
    createMeasureColumn(cm1ValOD, { ignoreComparison: true }),
    createMeasureColumn(cm1RateOD, { className: 'font-bold' }),
    createMeasureColumn(slsFeeValOD, { ignoreComparison: true }),
    createMeasureColumn(logCstValOD, { ignoreComparison: true }),
    createMeasureColumn(cm2ValOD, { ignoreComparison: true }),
    createMeasureColumn(cm2RateOD, {
      className: row => cn('font-bold', statusColorClass((row as unknown as Row<LensRow>).original.status).text),
    }),
  ];
}

export function buildStatusColumn(intl: IntlShape): ColumnDef<ProfitabilityBreakdownRow> {
  const title = intl.formatMessage({ id: 'profitability.drillDown.column.status' });
  const tooltip = intl.formatMessage({ id: 'profitability.drillDown.status.tooltip' });
  return {
    accessorKey: 'status',
    header: () => <Tooltip tooltipContent={tooltip}>{title}</Tooltip>,
    cell: context => <StatusCell status={context.row.original.status} />,
    sortingFn: (rowA, rowB) => statusSortValue(rowA.original.status) - statusSortValue(rowB.original.status),
    meta: { sortTitleId: 'profitability.drillDown.column.status' },
  };
}
